import {
  PopupLeadCapture,
  PopupLeadCaptureProps,
} from '@src/components/popup-lead-capture/PopupLeadCapture.component'
import {
  PopupOfferCampaign,
  PopupOfferCampaignProps,
} from '@src/components/popup-offer-campaign/PopupOfferCampaign.component'
import { PopupSimple, PopupSimpleProps } from '@src/components/popup-simple/PopupSimple.component'
import { PopupSms, PopupSmsProps } from '@src/components/popup-sms/PopupSms.component'
import { usePopup } from '@src/hooks/usePopup'
import { PopupStatuses } from '@src/store/popup/popup.slice'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { PopupFixed, PopupFixedProps } from '../ab-381/popup-fixed/PopupFixed.component'
import {
  PopupFixedAccordion,
  PopupFixedAccordionProps,
} from '../ab-381/popup-fixed-accordion/PopupFixedAccordion.component'

export enum PopupTriggers {
  Delay = 'Delay',
  Scroll = 'Scroll',
}

/**
 * @example
 * // For example, to trigger a popup at 5% scroll depth:
 * <Popup trigger={{ type: PopupTriggers.Scroll, scrollPercentage: 5 }} />
 **/
export interface PopupTriggerScroll {
  type: PopupTriggers.Scroll
  scrollPercentage: number
}
/**
 * @example
 * // For example, to trigger a popup 3 second after page load:
 * <Popup trigger={{ type: PopupTriggers.Delay, delayMs: 3000 }} />
 */

export interface PopupTriggerDelay {
  type: PopupTriggers.Delay
  delayMs: number
}

export interface PopupContent {
  placement: string
  name: string
  popup: [
    | PopupLeadCaptureProps
    | PopupOfferCampaignProps
    | PopupSmsProps
    | PopupSimpleProps
    | PopupFixedAccordionProps
    | PopupFixedProps,
  ]
}

export enum DisplayRule {
  HideAfterFirstImpression = 'HideAfterFirstImpression',
  HideAfterFirstClose = 'HideAfterFirstClose',
}

export interface PopupProps extends PopupContent {
  trigger: PopupTriggerScroll | PopupTriggerDelay
  status?: PopupStatuses
  openPopup?: (id: string) => void
  closePopup?: () => void
}

export function Popup({ name, popup: [popup], trigger }: PopupProps): ReactNode {
  const isFixedPopup = ['fixed_accordion_popup', 'fixed_popup'].includes(popup._modelApiKey)
  const [popupNode, setPopupNode] = useState<React.ReactNode>(null)

  const { status, open, close, portal } = usePopup({
    name,
    children: popupNode,
    hasOverlay: !isFixedPopup,
    displayRule: getDisplayRule(popup._modelApiKey),
    hasBasicCSS: !isFixedPopup,
    animation: isFixedPopup ? 'fade' : 'slide',
  })

  if (trigger.type === PopupTriggers.Delay) {
    useEffect(() => {
      if (typeof window === 'undefined') {
        return
      }
      const timeout = setTimeout(() => open(), trigger.delayMs)
      return () => {
        // Cancel the timeout if <Popup /> unmounts before it fires. Otherwise
        // if the user navigates to another page before the popup appears, the
        // onTrigger() callback will still fire, leading to "Warning: Can't
        // perform a React state update on an unmounted component."
        clearTimeout(timeout)
      }
    }, [])
  }

  if (trigger.type === PopupTriggers.Scroll) {
    function handleScroll(): void {
      // This is a quick cross-browser scroll percentage implementation based
      // on https://stackoverflow.com/a/8028584.
      const { body, documentElement } = document
      const { clientHeight } = documentElement
      const scrollTop = documentElement.scrollTop || body.scrollTop
      const scrollHeight = documentElement.scrollHeight || body.scrollHeight
      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100

      if (scrollPercentage > (trigger as PopupTriggerScroll).scrollPercentage) {
        window.removeEventListener('scroll', handleScroll)
        open()
      }
    }

    useEffect(() => {
      if (typeof window === 'undefined') {
        return
      }
      window.addEventListener('scroll', handleScroll)
      return () => {
        // Very important to remove the scroll listener here when <Popup />
        // unmounts otherwise it will continue listening if the user navigates
        // to another page on the site before reaching the scroll threshold to
        // trigger the poup. If they then reach the scroll threshold on that
        // page the callbacks here will still fire even though the component is
        // no longer mounted, which would lead to the following error:
        // "Warning: Can't perform a React state update on an unmounted component."
        window.removeEventListener('scroll', handleScroll)
      }
    }, [])
  }

  const [popupImplementation, setPopupImplementation] = useState<React.ReactNode>(null)

  const sharedProps = useMemo(
    () => ({
      name,
      status,
      isOpen: status === PopupStatuses.Open,
      handleDismiss: close,
    }),
    [name, status, close],
  )

  useEffect(() => {
    switch (popup._modelApiKey) {
      case 'lead_capture_popup': {
        setPopupImplementation(<PopupLeadCapture {...popup} {...sharedProps} />)
        break
      }
      case 'sms_popup': {
        setPopupImplementation(<PopupSms {...popup} {...sharedProps} />)
        break
      }
      case 'offer_campaign_popup': {
        setPopupImplementation(<PopupOfferCampaign {...popup} {...sharedProps} />)
        break
      }
      case 'simple_popup': {
        setPopupImplementation(<PopupSimple {...popup} {...sharedProps} />)
        break
      }
      case 'fixed_accordion_popup': {
        setPopupImplementation(<PopupFixedAccordion {...popup} {...sharedProps} />)
        break
      }
      case 'fixed_popup': {
        setPopupImplementation(<PopupFixed {...popup} {...sharedProps} />)
        break
      }
      default: {
        setPopupImplementation(null)
      }
    }
  }, [popup._modelApiKey, sharedProps])

  useEffect(() => {
    setPopupNode(popupImplementation)
  }, [popupImplementation])

  return <>{portal}</>
}

function getDisplayRule(
  modelApiKey:
    | 'fixed_accordion_popup'
    | 'fixed_popup'
    | 'lead_capture_popup'
    | 'offer_campaign_popup'
    | 'sms_popup'
    | 'simple_popup',
): DisplayRule {
  switch (modelApiKey) {
    case 'fixed_accordion_popup': {
      return DisplayRule.HideAfterFirstClose
    }
    case 'fixed_popup': {
      return DisplayRule.HideAfterFirstClose
    }
    default: {
      return DisplayRule.HideAfterFirstImpression
    }
  }
}
